import React, { useState, useEffect } from "react";
import {
	Button,
	CircularProgress,
	Box,
	Typography,
	TextField,
	InputAdornment,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useFirebase } from "../../context/ApiContext";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PublicIcon from "@mui/icons-material/Public";
import LockIcon from "@mui/icons-material/Lock";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LinkIcon from "@mui/icons-material/Link";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PaywallModal from "../Pricing/PaywallModal";
import EmailVerificationModal from "../EmailVerificationModal";
import {
	getUserState,
	canPerformAction,
	getCurrentVariant,
} from "../../utils/pricingHelpers";

const FORCE_EXPORT_AS_SUBSCRIBED = true;

const PRIMARY = "#6366F1";
const PRIMARY_GRADIENT = "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)";
const PRIMARY_GRADIENT_HOVER = "linear-gradient(135deg, #5558E3 0%, #7C4DE8 100%)";

const Export = ({ selectedProject, user }) => {
	const { enqueueSnackbar } = useSnackbar();
	const { createSharedGuide, getSharedGuides, deleteSharedGuide, isBlockedByEmailVerification } = useFirebase();
	const [isLoading, setIsLoading] = useState(false);
	const [isPublic, setIsPublic] = useState(false);
	const [sharedUrl, setSharedUrl] = useState(null);
	const [copied, setCopied] = useState(false);
	const [showPaywall, setShowPaywall] = useState(false);
	const [showEmailVerifyModal, setShowEmailVerifyModal] = useState(false);
	const [existingLinks, setExistingLinks] = useState([]); // { id, url, isPublic }[] para esta guía
	const [linksLoading, setLinksLoading] = useState(false);
	const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, isPublic } para modal borrar
	const [overwriteConfirmOpen, setOverwriteConfirmOpen] = useState(false);
	const [deletingId, setDeletingId] = useState(null);
	const [confirmModalContainer, setConfirmModalContainer] = useState(null);
	const variant = getCurrentVariant();

	const isPrebuilt = Boolean(selectedProject?.prebuiltGuideId);

	// Cargar enlaces existentes de esta guía al abrir o cambiar de proyecto
	useEffect(() => {
		if (!selectedProject?.id || !user) {
			setExistingLinks([]);
			setLinksLoading(false);
			return;
		}
		let cancelled = false;
		setLinksLoading(true);
		getSharedGuides().then((list) => {
			if (cancelled) return;
			setLinksLoading(false);
			if (!Array.isArray(list)) return;
			const forProject = list.filter((s) => s.projectId === selectedProject.id);
			setExistingLinks(forProject.map((s) => ({ id: s.id, url: s.url, isPublic: s.isPublic })));
		}).catch(() => {
			if (!cancelled) setLinksLoading(false);
		});
		return () => { cancelled = true; };
	}, [selectedProject?.id, user, getSharedGuides]);

	React.useEffect(() => {
		if (isPrebuilt && isPublic) {
			setIsPublic(false);
		}
	}, [isPrebuilt, isPublic]);

	const hasExistingOfType = existingLinks.some((l) => l.isPublic === isPublic);

	const doGenerateShareUrl = async () => {
		if (!selectedProject || !selectedProject.id) return;
		setIsLoading(true);
		setCopied(false);
		try {
			const result = await createSharedGuide(selectedProject.id, isPublic);
			if (result?.error === "TEMPLATE_CANNOT_BE_SHARED_PUBLICLY" || result?.error === "OFFICIAL_TEMPLATE_CANNOT_BE_SHARED_PUBLICLY") {
				enqueueSnackbar(
					"Las guías basadas en plantillas no se pueden compartir públicamente. Solo las guías creadas desde cero pueden tener enlace público.",
					{ variant: "warning" }
				);
			} else if (result && result.url) {
				setSharedUrl(result.url);
				getSharedGuides().then((list) => {
					if (!Array.isArray(list)) return;
					const forProject = list.filter((s) => s.projectId === selectedProject.id);
					setExistingLinks(forProject.map((s) => ({ id: s.id, url: s.url, isPublic: s.isPublic })));
				});
				enqueueSnackbar("URL generada correctamente", { variant: "success" });
			} else {
				enqueueSnackbar("Error al generar la URL", { variant: "error" });
			}
		} catch (error) {
			console.error("Error generating share URL:", error);
			enqueueSnackbar("Error al generar la URL", { variant: "error" });
		} finally {
			setIsLoading(false);
			setOverwriteConfirmOpen(false);
		}
	};

	const generateShareUrl = async () => {
		if (!selectedProject || !selectedProject.id) {
			enqueueSnackbar("No hay proyecto seleccionado", { variant: "error" });
			return;
		}
		if (isPublic && isBlockedByEmailVerification("share_public")) {
			setShowEmailVerifyModal(true);
			return;
		}
		const userState = getUserState(user);
		const canExport =
			FORCE_EXPORT_AS_SUBSCRIBED || canPerformAction(userState, "export");
		if (!canExport) {
			setShowPaywall(true);
			return;
		}
		if (hasExistingOfType) {
			setOverwriteConfirmOpen(true);
			return;
		}
		await doGenerateShareUrl();
	};

	const handleConfirmOverwrite = () => {
		doGenerateShareUrl();
	};

	const handleDeleteLink = (link) => {
		setDeleteConfirm({ id: link.id, isPublic: link.isPublic });
	};

	const handleConfirmDelete = async () => {
		if (!deleteConfirm) return;
		setDeletingId(deleteConfirm.id);
		try {
			const ok = await deleteSharedGuide(deleteConfirm.id);
			if (ok) {
				const list = await getSharedGuides();
				if (Array.isArray(list)) {
					const forProject = list.filter((s) => s.projectId === selectedProject.id);
					setExistingLinks(forProject.map((s) => ({ id: s.id, url: s.url, isPublic: s.isPublic })));
				}
				enqueueSnackbar("Enlace eliminado", { variant: "success" });
			} else {
				enqueueSnackbar("Error al eliminar el enlace", { variant: "error" });
			}
		} catch (e) {
			enqueueSnackbar("Error al eliminar el enlace", { variant: "error" });
		} finally {
			setDeletingId(null);
			setDeleteConfirm(null);
		}
	};

	const handleCopyUrl = () => {
		if (sharedUrl) {
			navigator.clipboard.writeText(sharedUrl);
			setCopied(true);
			enqueueSnackbar("URL copiada al portapapeles", { variant: "success" });
			setTimeout(() => setCopied(false), 2000);
		}
	};

	const userState = getUserState(user);
	const canExport =
		FORCE_EXPORT_AS_SUBSCRIBED || canPerformAction(userState, "export");

	const OptionCard = ({ selected, disabled, onClick, icon, title, description }) => (
		<Box
			onClick={disabled ? undefined : onClick}
			sx={{
				display: "flex",
				alignItems: "flex-start",
				gap: 1.5,
				p: 2,
				borderRadius: "12px",
				border: selected && !disabled
					? `2px solid ${PRIMARY}`
					: "2px solid rgba(0, 0, 0, 0.06)",
				background: selected && !disabled
					? "rgba(99, 102, 241, 0.04)"
					: disabled
						? "#fafafa"
						: "#fff",
				opacity: disabled ? 0.55 : 1,
				cursor: disabled ? "not-allowed" : "pointer",
				transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
				position: "relative",
				overflow: "hidden",
				"&:hover": disabled ? {} : {
					borderColor: selected ? PRIMARY : "rgba(99, 102, 241, 0.3)",
					background: selected
						? "rgba(99, 102, 241, 0.06)"
						: "rgba(99, 102, 241, 0.02)",
					transform: "translateY(-1px)",
					boxShadow: "0 4px 12px rgba(99, 102, 241, 0.08)",
				},
			}}
		>
			<Box
				sx={{
					width: 36,
					height: 36,
					borderRadius: "10px",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					background: selected && !disabled
						? PRIMARY_GRADIENT
						: disabled
							? "#e2e8f0"
							: "#f1f5f9",
					color: selected && !disabled ? "#fff" : disabled ? "#94a3b8" : "#64748b",
					flexShrink: 0,
					transition: "all 0.2s ease",
				}}
			>
				{icon}
			</Box>
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Typography
					sx={{
						fontSize: 13.5,
						fontWeight: 600,
						color: disabled ? "#94a3b8" : "#1a1a2e",
						lineHeight: 1.3,
						mb: 0.25,
						fontFamily: "'Inter', sans-serif",
					}}
				>
					{title}
				</Typography>
				<Typography
					sx={{
						fontSize: 12,
						color: disabled ? "#b0b8c4" : "#64748b",
						lineHeight: 1.5,
						fontFamily: "'Inter', sans-serif",
					}}
				>
					{description}
				</Typography>
			</Box>
			{selected && !disabled && (
				<CheckCircleIcon sx={{ fontSize: 18, color: PRIMARY, flexShrink: 0, mt: 0.25 }} />
			)}
		</Box>
	);

	return (
		<>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 2.5,
					fontFamily: "'Inter', sans-serif",
				}}
			>
				{/* Header */}
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
					<Box
						sx={{
							width: 40,
							height: 40,
							borderRadius: "12px",
							background: PRIMARY_GRADIENT,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							boxShadow: "0 4px 14px rgba(99, 102, 241, 0.3)",
						}}
					>
						<LinkIcon sx={{ color: "#fff", fontSize: 20 }} />
					</Box>
					<Box>
						<Typography
							sx={{
								fontSize: 18,
								fontWeight: 700,
								color: "#1a1a2e",
								lineHeight: 1.3,
								fontFamily: "'Inter', sans-serif",
								letterSpacing: "-0.01em",
							}}
						>
							Compartir guía
						</Typography>
						<Typography
							sx={{
								fontSize: 14,
								color: "#64748b",
								lineHeight: 1.45,
								fontFamily: "'Inter', sans-serif",
							}}
						>
							Genera un enlace para compartir tu guía
						</Typography>
					</Box>
				</Box>

				{/* Banner guía basada en plantilla */}
				{isPrebuilt && (
					<Box
						sx={{
							display: "flex",
							alignItems: "flex-start",
							gap: 1.25,
							p: 1.75,
							borderRadius: "12px",
							background: "linear-gradient(135deg, rgba(245, 158, 11, 0.06) 0%, rgba(249, 115, 22, 0.06) 100%)",
							border: "1px solid rgba(245, 158, 11, 0.15)",
						}}
					>
						<InfoOutlinedIcon sx={{ fontSize: 18, color: "#d97706", mt: 0.1, flexShrink: 0 }} />
						<Typography
							sx={{
								fontSize: 12,
								color: "#92400e",
								lineHeight: 1.55,
								fontFamily: "'Inter', sans-serif",
							}}
						>
							Esta guía está basada en una plantilla de Viajes Molones.
							Solo las guías creadas desde cero pueden compartirse con un enlace público.
						</Typography>
					</Box>
				)}

				{/* Opciones */}
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
					<Typography
						sx={{
							fontSize: 12,
							fontWeight: 600,
							textTransform: "uppercase",
							letterSpacing: "0.05em",
							color: "#64748b",
							fontFamily: "'Inter', sans-serif",
						}}
					>
						Tipo de enlace
					</Typography>

					<OptionCard
						selected={isPublic}
						disabled={isPrebuilt}
						onClick={() => setIsPublic(true)}
						icon={<PublicIcon sx={{ fontSize: 18 }} />}
						title="Enlace público"
						description={
							isPrebuilt
								? "No disponible para guías basadas en plantillas"
								: "Cualquier persona con el enlace puede ver la guía, sin necesidad de iniciar sesión"
						}
					/>

					<OptionCard
						selected={!isPublic}
						disabled={false}
						onClick={() => setIsPublic(false)}
						icon={<LockIcon sx={{ fontSize: 18 }} />}
						title="Enlace privado"
						description="Solo tú puedes ver la guía desde tu cuenta"
					/>
				</Box>

				{/* Enlaces existentes de esta guía (loading o lista) — ocultar si acabamos de generar uno nuevo */}
				{!sharedUrl && (linksLoading || existingLinks.length > 0) && (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 1,
							p: 1.5,
							borderRadius: "12px",
							background: "rgba(0, 0, 0, 0.02)",
							border: "1px solid rgba(0, 0, 0, 0.06)",
						}}
					>
						<Typography
							sx={{
								fontSize: 12,
								fontWeight: 600,
								textTransform: "uppercase",
								letterSpacing: "0.05em",
								color: "#64748b",
								fontFamily: "'Inter', sans-serif",
							}}
						>
							Enlaces actuales
						</Typography>
						{linksLoading ? (
							<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
								<CircularProgress size={20} sx={{ color: PRIMARY }} />
								<Typography sx={{ fontSize: 13, color: "#64748b", fontFamily: "'Inter', sans-serif" }}>
									Cargando enlaces...
								</Typography>
							</Box>
						) : (
							<Box component="span" sx={{ display: "block" }}>
								{existingLinks.map((link, idx) => (
									<Box
										key={link.id || idx}
										sx={{
											display: "flex",
											flexDirection: "column",
											gap: 1,
											marginBottom: idx === existingLinks.length - 1 ? 0 : 2,
										}}
									>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
											<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 100 }}>
												{link.isPublic ? (
													<PublicIcon sx={{ fontSize: 16, color: "#64748b" }} />
												) : (
													<LockIcon sx={{ fontSize: 16, color: "#64748b" }} />
												)}
												<Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", fontFamily: "'Inter', sans-serif" }}>
													{link.isPublic ? "Público" : "Privado"}
												</Typography>
											</Box>
											<TextField
											value={link.url}
											size="small"
											fullWidth
											InputProps={{
												readOnly: true,
												sx: { fontSize: 11.5 },
												endAdornment: (
													<InputAdornment position="end">
														<IconButton
															size="small"
															onClick={() => {
																navigator.clipboard.writeText(link.url);
																enqueueSnackbar("Enlace copiado", { variant: "success" });
															}}
															title="Copiar"
														>
															<ContentCopyIcon sx={{ fontSize: 16 }} />
														</IconButton>
														<IconButton
															size="small"
															onClick={() => handleDeleteLink(link)}
															disabled={deletingId === link.id}
															title="Eliminar enlace"
															sx={{ color: "#94a3b8", "&:hover": { color: "#dc2626", backgroundColor: "rgba(220, 38, 38, 0.08)" } }}
														>
															{deletingId === link.id ? (
																<CircularProgress size={16} />
															) : (
																<DeleteOutlineIcon sx={{ fontSize: 18 }} />
															)}
														</IconButton>
													</InputAdornment>
												),
											}}
											sx={{
												"& .MuiOutlinedInput-root": {
													backgroundColor: "#fff",
													borderRadius: "8px",
													fontFamily: "'Inter', sans-serif",
												},
											}}
										/>
										</Box>
										<Button
											variant="outlined"
											size="small"
											onClick={() => window.open(link.url, "_blank", "noopener,noreferrer")}
											sx={{
												width: "100%",
												alignSelf: "flex-start",
												textTransform: "none",
												fontSize: 12,
												fontWeight: 600,
												borderColor: `${PRIMARY}!important`,
												color: `${PRIMARY}!important`,
												fontFamily: "'Inter', sans-serif",
												"&:hover": {
													borderColor: PRIMARY,
													background: "rgba(99, 102, 241, 0.06)",
												},
											}}
										>
											Ver guía
										</Button>
									</Box>
								))}
							</Box>
						)}
					</Box>
				)}

				{/* Aviso overwrite: destacado cuando ya existe enlace del tipo seleccionado */}
				{hasExistingOfType && (
					<Box
						sx={{
							p: 1.5,
							borderRadius: "12px",
							background: "linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(249, 115, 22, 0.08) 100%)",
							border: "1px solid rgba(245, 158, 11, 0.4)",
							animation: "exportOverwritePulse 2s ease-in-out infinite",
							"@keyframes exportOverwritePulse": {
								"0%, 100%": { opacity: 1 },
								"50%": { opacity: 0.92 },
							},
						}}
					>
						<Typography
							sx={{
								fontSize: 12.5,
								fontWeight: 600,
								color: "#b45309",
								fontFamily: "'Inter', sans-serif",
								display: "flex",
								alignItems: "center",
								gap: 0.75,
							}}
						>
							<InfoOutlinedIcon sx={{ fontSize: 18, flexShrink: 0 }} />
							Si generas de nuevo, se reemplazará el enlace {isPublic ? "público" : "privado"} actual. El enlace anterior dejará de funcionar.
						</Typography>
					</Box>
				)}

				{/* CTA: outlined cuando ya existe enlace del mismo tipo */}
				<Button
					variant={hasExistingOfType ? "outlined" : "contained"}
					onClick={generateShareUrl}
					disabled={isLoading || !selectedProject}
					disableElevation
					sx={
						hasExistingOfType
								? {
									height: 44,
									borderRadius: "12px",
									border: "2px solid " + PRIMARY,
									background: "transparent",
									color: PRIMARY,
									textTransform: "none",
									fontWeight: 600,
									fontSize: 14,
									fontFamily: "'Inter', sans-serif",
									transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
									"&:hover": {
										borderWidth: "2px",
										borderColor: PRIMARY,
										background: "rgba(99, 102, 241, 0.08)",
										transform: "translateY(-1px)",
									},
									"&.Mui-disabled": {
										borderColor: "rgba(99, 102, 241, 0.3)",
										color: "rgba(99, 102, 241, 0.5)",
									},
								}
							: {
									height: 44,
									borderRadius: "12px",
									background: canExport ? PRIMARY_GRADIENT : "#e2e8f0",
									color: canExport ? "#fff" : "#94a3b8",
									textTransform: "none",
									fontWeight: 600,
									fontSize: 14,
									fontFamily: "'Inter', sans-serif",
									boxShadow: canExport ? "0 4px 14px rgba(99, 102, 241, 0.35)" : "none",
									transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
									"&:hover": {
										background: canExport ? PRIMARY_GRADIENT_HOVER : "#e2e8f0",
										color: canExport ? "#fff" : "#94a3b8",
										boxShadow: canExport ? "0 6px 20px rgba(99, 102, 241, 0.45)" : "none",
										transform: canExport ? "translateY(-1px)" : "none",
									},
									"&.Mui-disabled": {
										background: "rgba(99, 102, 241, 0.1)",
										color: "rgba(99, 102, 241, 0.4)",
									},
								}
					}
				>
					{isLoading ? (
						<CircularProgress size={18} sx={{ color: hasExistingOfType ? PRIMARY : "#fff" }} />
					) : (
						"Generar enlace"
					)}
				</Button>

				{/* URL resultado */}
				{sharedUrl && (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 1.5,
							p: 2,
							borderRadius: "12px",
							background: "rgba(99, 102, 241, 0.03)",
							border: `1px solid rgba(99, 102, 241, 0.12)`,
						}}
					>
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
							<CheckCircleIcon sx={{ fontSize: 15, color: "#10B981" }} />
							<Typography
								sx={{
									fontSize: 11.5,
									fontWeight: 600,
									color: "#10B981",
									textTransform: "uppercase",
									letterSpacing: "0.04em",
									fontFamily: "'Inter', sans-serif",
								}}
							>
								Enlace generado
							</Typography>
						</Box>
						<TextField
							value={sharedUrl}
							fullWidth
							size="small"
							InputProps={{
								readOnly: true,
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											onClick={handleCopyUrl}
											edge="end"
											size="small"
											sx={{
												color: copied ? PRIMARY : "#64748b",
												borderRadius: "8px",
												"&:hover": {
													background: "rgba(99, 102, 241, 0.08)",
												},
											}}
										>
											{copied ? (
												<CheckCircleIcon sx={{ fontSize: 18 }} />
											) : (
												<ContentCopyIcon sx={{ fontSize: 18 }} />
											)}
										</IconButton>
									</InputAdornment>
								),
							}}
							sx={{
								"& .MuiOutlinedInput-root": {
									backgroundColor: "#fff",
									borderRadius: "10px",
									fontSize: 12.5,
									fontFamily: "'Inter', sans-serif",
									"& fieldset": {
										borderColor: "rgba(0, 0, 0, 0.06)",
									},
									"&:hover fieldset": {
										borderColor: "rgba(99, 102, 241, 0.2)",
									},
									"&.Mui-focused fieldset": {
										borderColor: PRIMARY,
										borderWidth: "2px",
									},
								},
							}}
						/>
						<Typography
							sx={{
								fontSize: 11.5,
								color: "#64748b",
								lineHeight: 1.5,
								fontFamily: "'Inter', sans-serif",
							}}
						>
							{isPublic
								? "Cualquier persona con este enlace puede ver tu guía sin necesidad de iniciar sesión."
								: "Este enlace es privado. Solo tú puedes acceder con tu cuenta."}
						</Typography>
						<Button
							variant="outlined"
							size="small"
							onClick={() => window.open(sharedUrl, "_blank", "noopener,noreferrer")}
							sx={{
								width: "100%",
								alignSelf: "flex-start",
								textTransform: "none",
								fontSize: 12,
								fontWeight: 600,
								borderColor: `${PRIMARY}!important`,
								color: `${PRIMARY}!important`,
								fontFamily: "'Inter', sans-serif",
								"&:hover": {
									borderColor: PRIMARY,
									background: "rgba(99, 102, 241, 0.06)",
								},
							}}
						>
							Ver guía
						</Button>
					</Box>
				)}
			</Box>

			<PaywallModal
				open={showPaywall}
				onClose={() => setShowPaywall(false)}
				triggerAction="export"
				variant={variant}
			/>

			<EmailVerificationModal
				open={showEmailVerifyModal}
				onClose={() => setShowEmailVerifyModal(false)}
			/>

			{/* Contenedor con z-index alto para que los Dialogs de confirmación queden por encima de la modal de compartir */}
			<Box
				ref={setConfirmModalContainer}
				sx={{
					position: "fixed",
					zIndex: 1600,
					inset: 0,
					pointerEvents: deleteConfirm || overwriteConfirmOpen ? "auto" : "none",
				}}
				aria-hidden={!deleteConfirm && !overwriteConfirmOpen}
			/>

			{/* Modal confirmar eliminar enlace */}
			<Dialog
				open={Boolean(deleteConfirm)}
				onClose={() => setDeleteConfirm(null)}
				container={confirmModalContainer}
				PaperProps={{
					sx: { borderRadius: "16px", fontFamily: "'Inter', sans-serif" },
				}}
			>
				<DialogTitle sx={{ fontWeight: 700, fontSize: 18, fontFamily: "'Inter', sans-serif", color: "#1a1a2e" }}>
					Eliminar enlace
				</DialogTitle>
				<DialogContent>
					<DialogContentText sx={{ color: "#64748b", fontSize: 14, fontFamily: "'Inter', sans-serif", lineHeight: 1.5 }}>
						¿Eliminar el enlace {deleteConfirm?.isPublic ? "público" : "privado"}? Quien tenga el enlace guardado ya no podrá acceder a la guía.
					</DialogContentText>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
					<Button onClick={() => setDeleteConfirm(null)} sx={{ textTransform: "none", fontFamily: "'Inter', sans-serif", fontSize: 14 }}>
						Cancelar
					</Button>
					<Button
						variant="contained"
						color="error"
						onClick={handleConfirmDelete}
						disabled={deletingId != null}
						sx={{
							textTransform: "none",
							fontFamily: "'Inter', sans-serif",
							fontSize: 14,
							fontWeight: 500,
							color: "#fff!important",
							backgroundColor: "#d32f2f",
							border: "1px solid #d32f2f !important",
							"&:hover": { backgroundColor: "#b71c1c", color: "#fff!important" },
							"&.Mui-disabled": { color: "rgba(255,255,255,0.7)!important", backgroundColor: "#d32f2f" },
						}}
					>
						{deletingId != null ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Eliminar"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Modal confirmar reemplazar enlace (overwrite) */}
			<Dialog
				open={overwriteConfirmOpen}
				onClose={() => !isLoading && setOverwriteConfirmOpen(false)}
				container={confirmModalContainer}
				PaperProps={{
					sx: { borderRadius: "16px", fontFamily: "'Inter', sans-serif" },
				}}
			>
				<DialogTitle sx={{ fontWeight: 700, fontSize: 18, fontFamily: "'Inter', sans-serif", color: "#1a1a2e" }}>
					Reemplazar enlace
				</DialogTitle>
				<DialogContent>
					<DialogContentText sx={{ color: "#64748b", fontSize: 14, fontFamily: "'Inter', sans-serif", lineHeight: 1.5 }}>
						Se generará un nuevo enlace {isPublic ? "público" : "privado"} y el actual dejará de funcionar. ¿Continuar?
					</DialogContentText>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
					<Button
						onClick={() => setOverwriteConfirmOpen(false)}
						disabled={isLoading}
						sx={{ textTransform: "none", fontFamily: "'Inter', sans-serif", fontSize: 14 }}
					>
						Cancelar
					</Button>
					<Button
						variant="contained"
						onClick={handleConfirmOverwrite}
						disabled={isLoading}
						sx={{
							textTransform: "none",
							fontFamily: "'Inter', sans-serif",
							fontSize: 14,
							fontWeight: 500,
							color: "#fff",
							background: PRIMARY_GRADIENT,
							"&:hover": { background: PRIMARY_GRADIENT_HOVER, color: "#fff" },
							"&.Mui-disabled": { color: "rgba(255,255,255,0.7)" },
						}}
					>
						{isLoading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Generar enlace"}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default Export;
